

import { useEffect, useState } from "react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import {
  Check,
  Loader2,
  Copy,
  Download,
  ChevronDown,
  ChevronUp,
  FileText,
  Newspaper,
} from "lucide-react";
import { useToast } from "./hooks/use-toast";
import ArticlePreview from "./components/article-preview";
import { Textarea } from "./components/ui/textarea";
import { Toaster } from "./components/ui/sonner";
import {
  trackArticleGenerated,
  trackArticleGenerationFailed,
  trackPageOpened,
  trackRedditUrlSubmitted,
} from "./lib/analytics";


interface GeneratedArticle {
  title: string;
  subtitle: string;
  body: string;
  sourceUrl: string;
  generatedAt: string;
}

const isRedditPostUrl = (value: string) => {
  try {
    const hostname = new URL(value).hostname.toLowerCase();
    return hostname.endsWith("reddit.com") || hostname === "redd.it" || hostname.endsWith(".redd.it");
  } catch {
    return false;
  }
};

const readApiError = async (response: Response, fallback: string) => {
  try {
    const payload = await response.json();
    if (payload?.details) {
      return `${payload.error || fallback}: ${payload.details}`;
    }
    return payload?.error || fallback;
  } catch {
    return fallback;
  }
};

export default function RedaitApp() {
  const [url, setUrl] = useState("");
  const [tone, setTone] = useState("neutral");
  const [length, setLength] = useState("medium");
  const [language, setLanguage] = useState("english");
  const [isGenerating, setIsGenerating] = useState(false);
  const [article, setArticle] = useState<GeneratedArticle | null>(null);
  const [error, setError] = useState("");
  const { toast } = useToast();
  const [customPrompt, setCustomPrompt] = useState("");
  const [showPromptEditor, setShowPromptEditor] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    trackPageOpened();
  }, []);

  const handleGenerate = async () => {
    const submittedUrl = url.trim();
    const generationSettings = {
      redditUrl: submittedUrl,
      tone,
      length,
      language,
      customPrompt,
    };

    if (!submittedUrl) {
      setError("Please enter a Reddit URL");
      return;
    }
    if (!isRedditPostUrl(submittedUrl)) {
      setError("Please enter a valid Reddit URL");
      return;
    }
    setIsGenerating(true);
    setError("");
    setArticle(null);
    trackRedditUrlSubmitted(generationSettings);

    try {
      // First, scrape the Reddit post
      const scrapeResponse = await fetch("/api/scrape", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: submittedUrl }),
      });

      if (!scrapeResponse.ok) {
        throw new Error(await readApiError(scrapeResponse, "Failed to scrape Reddit post"));
      }
      const scrapedData = await scrapeResponse.json();

      // Then, generate the article
      const generateResponse = await fetch("/api/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...scrapedData,
          tone,
          length,
          language,
          sourceUrl: submittedUrl,
          customPrompt: customPrompt.trim() || undefined,
        }),
      });

      if (!generateResponse.ok) {
        throw new Error(await readApiError(generateResponse, "Failed to generate article"));
      }
      const generatedArticle = await generateResponse.json();
      setArticle(generatedArticle);
      trackArticleGenerated(generatedArticle, generationSettings);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "An error occurred";
      setError(errorMessage);
      trackArticleGenerationFailed(generationSettings, errorMessage);
      toast({
        title: "Generation Failed",
        description: errorMessage,
        variant: "destructive",
      });
    }
    finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      toast({
        title: "Copy Failed",
        description: "Failed to copy to clipboard.",
        variant: "destructive",
      });
    }
  };

  const downloadAsPdf = async () => {
    if (!article) return;

    try {
      const response = await fetch("/api/generate-pdf", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(article),
      });

      if (!response.ok) {
        throw new Error("Failed to generate PDF");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const contentDisposition = response.headers.get("Content-Disposition");
      let filename = "article.pdf";
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }

      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      toast({
        title: "PDF Downloaded!",
        description: "Your article has been downloaded as a PDF.",
      });
    } catch (err) {
      toast({
        title: "PDF Download Failed",
        description: err instanceof Error ? err.message : "An error occurred while downloading the PDF.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="redait-shell">
      <Toaster />
      <header className="redait-nav" aria-label="Primary">
        <a className="redait-nav__mark" href="#top" aria-label="Redait home">
          Red<span className="redait-nav__mark-ai">ai</span>t
        </a>
      </header>

      <main id="top" className="redait-main">
        <section className="redait-workbench" aria-labelledby="hero-title">
          <div id="generator" className="redait-panel redait-panel--form">
            <header className="redait-hero">
              <h1 id="hero-title">Thread to draft.</h1>
              <p>Paste one public Reddit post. Redait returns an article draft you can edit, copy, or export.</p>
            </header>

            <div className="redait-form">
              <div className="redait-field">
                <Label htmlFor="url" className="redait-label">
                  Reddit post URL
                </Label>
                <Input
                  id="url"
                  placeholder="https://www.reddit.com/r/..."
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  aria-invalid={Boolean(error)}
                  aria-describedby="url-helper"
                  className="redait-input"
                />
                <p id="url-helper" className={error ? "redait-helper redait-helper--error" : "redait-helper"}>
                  {error || "Use a full reddit.com URL. Private or deleted posts cannot be read."}
                </p>
              </div>

              <div className="redait-prompt">
                <div className="redait-prompt__top">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowPromptEditor(!showPromptEditor)}
                    className="redait-ghost-button"
                    aria-expanded={showPromptEditor}
                  >
                    {showPromptEditor ? (
                      <>
                        <ChevronUp className="h-3 w-3" aria-hidden="true" />
                        Hide options
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-3 w-3" aria-hidden="true" />
                        Options
                      </>
                    )}
                  </Button>
                </div>
                {showPromptEditor && (
                  <div className="redait-prompt__body">
                    <div className="redait-options">
                      <div className="redait-field">
                        <Label htmlFor="tone" className="redait-label">
                          Tone
                        </Label>
                        <Select value={tone} onValueChange={setTone}>
                          <SelectTrigger id="tone" className="redait-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="redait-select-menu">
                            <SelectItem value="neutral">Neutral report</SelectItem>
                            <SelectItem value="opinion">Opinion piece</SelectItem>
                            <SelectItem value="satirical">Satirical angle</SelectItem>
                            <SelectItem value="investigative">Investigative</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="redait-field">
                        <Label htmlFor="length" className="redait-label">
                          Length
                        </Label>
                        <Select value={length} onValueChange={setLength}>
                          <SelectTrigger id="length" className="redait-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="redait-select-menu">
                            <SelectItem value="short">Short</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="long">Long</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="redait-field">
                        <Label htmlFor="language" className="redait-label">
                          Language
                        </Label>
                        <Select value={language} onValueChange={setLanguage}>
                          <SelectTrigger id="language" className="redait-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="redait-select-menu">
                            <SelectItem value="english">English</SelectItem>
                            <SelectItem value="croatian">Croatian</SelectItem>
                            <SelectItem value="spanish">Spanish</SelectItem>
                            <SelectItem value="french">French</SelectItem>
                            <SelectItem value="german">German</SelectItem>
                            <SelectItem value="italian">Italian</SelectItem>
                            <SelectItem value="portuguese">Portuguese</SelectItem>
                            <SelectItem value="dutch">Dutch</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <Label htmlFor="custom-prompt" className="redait-label">
                      Extra instruction
                    </Label>
                    <Textarea
                      id="custom-prompt"
                      placeholder="Focus on the economic implications, or write from a technology desk perspective."
                      value={customPrompt}
                      onChange={(e) => setCustomPrompt(e.target.value)}
                      rows={4}
                      className="redait-textarea"
                    />
                    <p className="redait-helper">
                      These instructions are added to the selected tone, length, and language.
                    </p>
                  </div>
                )}
              </div>

              <Button
                onClick={handleGenerate}
                disabled={isGenerating}
                className="redait-primary-button"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
                    Generating
                  </>
                ) : (
                  <>
                    Generate draft
                  </>
                )}
              </Button>
            </div>
          </div>

          <div id="preview" className="redait-panel redait-panel--preview" aria-live="polite">
            <div className="redait-panel__header">
              <span className="redait-panel__icon" aria-hidden="true">
                <FileText />
              </span>
              <div>
                <h2>Draft</h2>
                <p>The generated article appears here.</p>
              </div>
            </div>

            <div className="redait-preview-body">
              {isGenerating ? (
                <div className="redait-loading">
                  <Loader2 className="h-8 w-8 animate-spin" aria-hidden="true" />
                  <div>
                    <p>Drafting article.</p>
                    <span>This can take 10-30 seconds.</span>
                  </div>
                </div>
              ) : article ? (
                <div className="redait-article-wrap">
                  <ArticlePreview article={article} />
                  <div className="redait-actions">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(`# ${article.title}\n\n## ${article.subtitle}\n\n${article.body}`)}
                      className="redait-secondary-button"
                      data-state={copied ? "success" : "default"}
                    >
                      {copied ? <Check className="h-4 w-4" aria-hidden="true" /> : <Copy className="h-4 w-4" aria-hidden="true" />}
                      {copied ? "Copied" : "Copy Article"}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={downloadAsPdf}
                      className="redait-secondary-button"
                    >
                      <Download className="h-4 w-4" aria-hidden="true" />
                      Download PDF
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="redait-empty">
                  <Newspaper className="h-10 w-10" aria-hidden="true" />
                  <div>
                    <p>No draft yet.</p>
                    <span>Paste a Reddit URL to begin.</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>

      <footer className="redait-footer">
        <p>Redait · Draft from public Reddit posts · Review before publishing</p>
      </footer>
    </div>
  );
}
